import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2 } from "lucide-react";
import { admin } from "@/lib/api";
import { Field, TabHeader, inputCls } from "@/admin/ui";

const BUTTON_FIELDS = [
  ["hero_primary_label", "Hero primary button label"],
  ["hero_primary_link", "Hero primary button link"],
  ["hero_secondary_label", "Hero secondary button label"],
  ["hero_secondary_link", "Hero secondary button link"],
  ["cta_title", "CTA section title"],
  ["cta_subtitle", "CTA section subtitle"],
  ["cta_button_label", "CTA button label"],
  ["cta_button_link", "CTA button link"],
];

export default function SettingsTab() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    admin.getSettings().then(setForm).catch(() => toast.error("Could not load settings"));
  }, []);

  if (!form) return null;

  const setA = (k) => (e) =>
    setForm((f) => ({ ...f, announcement: { ...f.announcement, [k]: e.target ? e.target.value : e } }));
  const setB = (k) => (e) =>
    setForm((f) => ({ ...f, buttons: { ...f.buttons, [k]: e.target.value } }));

  const save = async () => {
    setSaving(true);
    try {
      await admin.saveSettings(form);
      toast.success("Saved — live on the site now");
    } catch {
      toast.error("Save failed — please try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-settings-tab">
      <TabHeader title="Announcement & Buttons" description="Update the homepage announcement bar and button text without touching code." />

      <h2 className="mt-10 font-heading text-lg font-semibold text-foreground">Homepage announcement</h2>
      <div className="mt-4 grid gap-5 rounded-3xl border border-border bg-card p-7 shadow-soft">
        <label className="flex items-center gap-2.5 text-sm text-foreground">
          <input
            type="checkbox"
            checked={!!form.announcement?.enabled}
            onChange={(e) => setForm((f) => ({ ...f, announcement: { ...f.announcement, enabled: e.target.checked } }))}
            data-testid="announcement-enabled-checkbox"
            className="h-4 w-4 accent-terracotta-500"
          />
          Show announcement bar at the top of every page
        </label>
        <Field label="Announcement text">
          <input className={inputCls} value={form.announcement?.text || ""} onChange={setA("text")} data-testid="announcement-text-input" placeholder="e.g. Now enrolling for fall parent coaching" />
        </Field>
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Link label" hint="Optional"><input className={inputCls} value={form.announcement?.link_label || ""} onChange={setA("link_label")} data-testid="announcement-link-label-input" placeholder="Learn more" /></Field>
          <Field label="Link URL" hint="Optional"><input className={inputCls} value={form.announcement?.link_url || ""} onChange={setA("link_url")} data-testid="announcement-link-url-input" placeholder="/book" /></Field>
        </div>
      </div>

      <h2 className="mt-10 font-heading text-lg font-semibold text-foreground">Buttons & links</h2>
      <div className="mt-4 grid gap-5 rounded-3xl border border-border bg-card p-7 shadow-soft sm:grid-cols-2">
        {BUTTON_FIELDS.map(([key, label]) => (
          <Field key={key} label={label}>
            <input className={inputCls} value={form.buttons?.[key] || ""} onChange={setB(key)} data-testid={`settings-${key.replace(/_/g, "-")}-input`} />
          </Field>
        ))}
      </div>

      <button onClick={save} disabled={saving} data-testid="admin-settings-save" className="btn-primary mt-8 disabled:opacity-60">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        {saving ? "Saving…" : "Save Changes"}
      </button>
    </div>
  );
}
